import { LIST_TOKENS } from "./list-tokens";
import type { ListItemPosition, ListSelectionMode } from "./list-types";

export function getListItemHeight(
	hasOverline: boolean,
	supportingTextLines: 0 | 1 | 2,
): 56 | 72 | 88 {
	if (hasOverline && supportingTextLines > 0)
		return LIST_TOKENS.threeLineHeight;
	if (supportingTextLines === 2) return LIST_TOKENS.threeLineHeight;
	if (hasOverline || supportingTextLines === 1)
		return LIST_TOKENS.twoLineHeight;
	return LIST_TOKENS.oneLineHeight;
}

export function shouldTopAlign(height: number): boolean {
	return height >= LIST_TOKENS.threeLineHeight;
}

export function getExpressiveShape(
	state:
		| "resting"
		| "hovered"
		| "focused"
		| "pressed"
		| "selected"
		| "dragged"
		| "disabled",
): number {
	return LIST_TOKENS.shape[state] ?? LIST_TOKENS.shape.resting;
}

export function getListItemAriaProps(
	mode: ListSelectionMode,
	selected: boolean,
	disabled: boolean,
): Record<string, string | boolean | undefined> {
	const props: Record<string, string | boolean | undefined> = {};

	if (disabled) props["aria-disabled"] = true;

	if (mode === "single-select" || mode === "multi-select") {
		props.role = "option";
		props["aria-selected"] = selected;
	} else if (mode === "single-action" || mode === "multi-action") {
		props.role = "button";
	} else {
		props.role = "listitem";
	}

	return props;
}

export function getListItemPosition(
	index: number | undefined,
	total: number,
): ListItemPosition {
	if (index === undefined || total <= 1) return "solo";
	if (index === 0) return "leading";
	if (index === total - 1) return "trailing";
	return "middle";
}

type RadiusCorners = { tl: number; tr: number; br: number; bl: number };

export function getAsymmetricRadius(
	position: ListItemPosition,
	outerRadius: number,
	innerRadius: number,
	stateRadius: number,
	active = false,
): RadiusCorners {
	const outer = Math.max(outerRadius, stateRadius);
	const inner = active ? stateRadius : Math.min(innerRadius, stateRadius);

	if (active) {
		const target = Math.max(outerRadius, stateRadius);
		switch (position) {
			case "solo":
				return { tl: target, tr: target, br: target, bl: target };
			case "leading":
				return { tl: target, tr: target, br: stateRadius, bl: stateRadius };
			case "trailing":
				return { tl: stateRadius, tr: stateRadius, br: target, bl: target };
			default:
				return {
					tl: stateRadius,
					tr: stateRadius,
					br: stateRadius,
					bl: stateRadius,
				};
		}
	}

	switch (position) {
		case "solo":
			return { tl: outer, tr: outer, br: outer, bl: outer };
		case "leading":
			return { tl: outer, tr: outer, br: inner, bl: inner };
		case "trailing":
			return { tl: inner, tr: inner, br: outer, bl: outer };
		default:
			return {
				tl: stateRadius,
				tr: stateRadius,
				br: stateRadius,
				bl: stateRadius,
			};
	}
}
